import React, { useEffect, useState } from "react";
import SortedCountryDropdown from "../../utils/sortedCountryDropdown";
import { useQueryClient } from "react-query";
import ENDPOINTS from "../../services/endPoints";
import { useGetKeyHostDetails } from "../../hooks/keyHostManagementApi/getKeyHostDetails";
import { useGetKeyHostBoxes } from "../../hooks/keyHostManagementApi/getKeyHostBoxes";
import { useGetKeyHostKeys } from "../../hooks/keyHostManagementApi/getKeyHostKeys";
import { useUpdateKeyHostUser } from "../../hooks/keyHostManagementApi/updateKeyHostUser";
import { useDeleteKeyHostUser } from "../../hooks/keyHostManagementApi/deleteKeyHostUser";
import { useAddKeyHostUser } from "../../hooks/keyHostManagementApi/addKeyHostUser";
import { useUpdateKeyHost } from "../../hooks/keyHostManagementApi/updateKeyHost";
import { useUpdateKeyHostStatus } from "../../hooks/keyHostManagementApi/updateStatus";
import { useResendPassword } from "../../hooks/keyHostManagementApi/resendHostPassword";
import { useGetBoxList } from "../../hooks/boxManagementApi/getBoxList";
import { useUpdateBox } from "../../hooks/boxManagementApi/updateBox";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Header from "../../components/content_wrapper/header";
import { EditSvg, EyeSvg } from "../../utils/svgComponent";
import moment from "moment";
import { toast } from "react-toastify";
import CustomLaoder from "../../components/customLoader";

const KeyHostDetailScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: urlId } = useParams<{ id: string }>();
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [isEditingHost, setIsEditingHost] = useState(false);
  const [editedHostInfo, setEditedHostInfo] = useState<any>(null);
  const [selectedBox, setSelectedBox] = useState<any>(null);
  const [showAssignBoxModal, setShowAssignBoxModal] = useState(false);
  const [boxesPage, setBoxesPage] = useState(1);
  const [keysPage, setKeysPage] = useState(1);
  const [newUser, setNewUser] = useState<any>({
    userName: '',
    email: '',
    loginId: '',
    password: '',
    role: 'staff'
  });
  const queryClient = useQueryClient();
  const { mutateAsync: updateUser, isLoading: isUpdating } = useUpdateKeyHostUser();
  const { mutateAsync: deleteUser, isLoading: _isDeleting } = useDeleteKeyHostUser();
  const { mutateAsync: addUser, isLoading: isAdding } = useAddKeyHostUser();
  const { mutateAsync: updateKeyHost, isLoading: isUpdatingHost } = useUpdateKeyHost();
  const { mutateAsync: updateBox, isLoading: isAssigningBox } = useUpdateBox();
  const { mutateAsync: updateStatus, isLoading: isTogglingStatus } = useUpdateKeyHostStatus();
  const { mutateAsync: resendPass, isLoading: _isResending } = useResendPassword();
  
  // Get available boxes for assignment
  const { data: boxListData } = useGetBoxList({
    page: "1",
    limit: "100",
    search: ""
  });

  // Get ID from either location state or URL params
  const data = location.state;
  const keyHostId = data?.id || urlId;
  
  // Generate random string for login ID and password
  const generateRandomString = (length: number = 12): string => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };
  
  const handleGenerateLoginId = () => {
    const randomLoginId = generateRandomString(10);
    setNewUser({...newUser, loginId: randomLoginId});
    toast.success('Login ID generated');
  };
  
  const handleGeneratePassword = () => {
    const randomPassword = generateRandomString(16);
    setNewUser({...newUser, password: randomPassword});
    toast.success('Password generated');
  };
  
  const handleAddUser = async () => {
    try {
      // Validate required fields
      if (!newUser.userName || !newUser.email || !newUser.loginId || !newUser.password) {
        toast.error("Please fill in all required fields");
        return;
      }

      // Use the keyHostId from the URL params (this is the key_hosts store ID)
      // This works whether or not there are existing users
      if (!keyHostId) {
        toast.error("Unable to determine key host. Please refresh and try again.");
        return;
      }

      // Prepare data for API
      const addData = {
        key_host_id: keyHostId,
        user_name: newUser.userName,
        email: newUser.email,
        user_login_id: newUser.loginId,
        password: newUser.password,
        role: newUser.role
      };

      const res: any = await addUser(addData);
      
      if (res?.success) {
        toast.success(res?.message || "User added successfully");
        // Close modal
        const closeButton: any = document.getElementById("close-add-btn");
        closeButton?.click();
        // Refresh data
        refetch();
        // Clear form
        setNewUser({
          userName: '',
          email: '',
          loginId: '',
          password: '',
          role: 'staff'
        });
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Error adding user");
    }
  };
  
  // Redirect to listing if no ID is provided
  useEffect(() => {
    if (!keyHostId) {
      toast.error("No key host ID provided. Redirecting to key host listing.");
      navigate("/keyhostmanagement");
    }
  }, [keyHostId, navigate]);
  
  const { data: GetKeyHostDetail, isLoading, refetch } = useGetKeyHostDetails({
    id: keyHostId,
  });
  const hostDetail = GetKeyHostDetail?.data?.keyHostDetails;

  // Separate paginated queries for boxes and keys
  const {
    data: boxesData,
    isLoading: isLoadingBoxes,
    refetch: refetchBoxes,
  } = useGetKeyHostBoxes({ id: keyHostId, page: boxesPage });

  const {
    data: keysData,
    isLoading: isLoadingKeys,
    refetch: _refetchKeys,
  } = useGetKeyHostKeys({ id: keyHostId, page: keysPage });

  const boxesTotalPages = boxesData?.data?.totalPages || 1;
  const boxesTotalCount = boxesData?.data?.totalCount || 0;
  const keysTotalPages = keysData?.data?.totalPages || 1;
  const keysTotalCount = keysData?.data?.totalCount || 0;
  
  // Initialize edited host info when data loads
  useEffect(() => {
    if (hostDetail && !editedHostInfo) {
      setEditedHostInfo({
        storeName: hostDetail.storeName || '',
        address: hostDetail.address || '',
        countryName: hostDetail.countryName || '',
        phoneNumber: hostDetail.phoneNumber || '',
        countryCode: hostDetail.countryCode || '',
        storeInfo: hostDetail.storeInfo || '',
        activeTimes: hostDetail.activeTimes || []
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostDetail]);
  
  const handleEditHost = () => {
    setIsEditingHost(true);
    setEditedHostInfo({
      storeName: hostDetail.storeName || '',
      address: hostDetail.address || '',
      countryName: hostDetail.countryName || '',
      phoneNumber: hostDetail.phoneNumber || '',
      countryCode: hostDetail.countryCode || '',
      storeInfo: hostDetail.storeInfo || '',
      activeTimes: hostDetail.activeTimes || []
    });
  };
  
  const handleCancelEditHost = () => {
    setIsEditingHost(false);
    setEditedHostInfo(null);
  };
  
  const handleSaveHostInfo = async () => {
    try {
      if (!editedHostInfo) {
        toast.error("No changes to save");
        return;
      }

      // Validate required fields
      if (!editedHostInfo.storeName || !editedHostInfo.address || !editedHostInfo.phoneNumber) {
        toast.error("Please fill in all required fields");
        return;
      }

      // Prepare data for API - include all required fields from validation
      const updateData = {
        id: keyHostId,
        user_name: hostDetail?.userName || '',
        email: hostDetail?.email || '',
        country_name: editedHostInfo.countryName || '',
        country_code: editedHostInfo.countryCode,
        phone_number: editedHostInfo.phoneNumber,
        longitude: 0,  // Default value if not available
        latitude: 0,   // Default value if not available
        address: editedHostInfo.address,
        store_name: editedHostInfo.storeName,
        store_info: editedHostInfo.storeInfo,
        active_time: editedHostInfo.activeTimes,  // Note: API expects 'active_time' (singular)
        box_id: []  // Empty array for box assignments (handled separately)
      };

      const res: any = await updateKeyHost(updateData);
      
      if (res?.success) {
        toast.success(res?.message || "Key host updated successfully");
        setIsEditingHost(false);

        // Immediately patch the React Query cache with the saved values.
        // This bypasses the HTTP 304 "Not Modified" response that the browser
        // returns when the cached GET response hasn't changed (e.g. for stores
        // created before the country_name field was introduced).
        queryClient.setQueryData(
          [ENDPOINTS.GET_KEY_HOST_DETAILS, { id: keyHostId }],
          (old: any) => ({
            ...old,
            data: {
              ...old?.data,
              keyHostDetails: {
                ...old?.data?.keyHostDetails,
                storeName: editedHostInfo.storeName,
                address: editedHostInfo.address,
                countryName: editedHostInfo.countryName,
                phoneNumber: editedHostInfo.phoneNumber,
                countryCode: editedHostInfo.countryCode,
                storeInfo: editedHostInfo.storeInfo,
                activeTimes: editedHostInfo.activeTimes,
              },
            },
          })
        );

        // Reset local edit state so the next "Edit Info" picks up fresh values
        setEditedHostInfo(null);
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Error updating key host");
    }
  };
  
  const handleEditUser = (user: any) => {
    setSelectedUser(user);
  };
  
  const handleUpdateUser = async () => {
    try {
      if (!selectedUser) {
        toast.error("No user selected");
        return;
      }

      // Validate required fields
      if (!selectedUser.userName || !selectedUser.email || !selectedUser.userLoginId) {
        toast.error("Please fill in all required fields");
        return;
      }

      // Prepare data for API
      const updateData: any = {
        id: selectedUser.Id,  // Note: API transformer converts _id to Id
        user_name: selectedUser.userName,
        email: selectedUser.email,
        user_login_id: selectedUser.userLoginId,
        role: selectedUser.role,
        status: selectedUser.status
      };

      // Only include password if it was changed
      if (selectedUser.password && selectedUser.password.trim() !== '') {
        updateData.password = selectedUser.password;
      }

      const res: any = await updateUser(updateData);
      
      if (res?.success) {
        toast.success(res?.message || "User updated successfully");
        // Close modal
        const closeButton: any = document.getElementById("close-edit-btn");
        closeButton?.click();
        // Refresh data
        refetch();
        // Clear selected user
        setSelectedUser(null);
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Error updating user");
    }
  };
  
  const handleDeleteUser = async (user: any) => {
    if (window.confirm(`Are you sure you want to delete user "${user.userName}"? This action cannot be undone.`)) {
      try {
        const res: any = await deleteUser({ id: user.Id });  // Note: API transformer converts _id to Id
        
        if (res?.success) {
          toast.success(res?.message || "User deleted successfully");
          // Refresh data
          refetch();
        }
      } catch (error: any) {
        toast.error(error?.response?.data?.message || "Error deleting user");
      }
    }
  };
  
  const handleUpdateActiveTime = (index: number, field: string, value: any) => {
    const updatedTimes = [...(editedHostInfo?.activeTimes || [])];
    updatedTimes[index] = {
      ...updatedTimes[index],
      [field]: value
    };
    setEditedHostInfo({
      ...editedHostInfo,
      activeTimes: updatedTimes
    });
  };
  
  const handleAssignBox = () => {
    setShowAssignBoxModal(true);
  };
  
  const handleConfirmAssignBox = async () => {
    try {
      if (!selectedBox) {
        toast.error("Please select a box");
        return;
      }

      // Update box with key host assignment
      const updateData = {
        id: selectedBox,
        key_host_id: keyHostId
      };

      const res: any = await updateBox(updateData);
      
      if (res?.success) {
        toast.success(res?.message || "Box assigned successfully");
        setShowAssignBoxModal(false);
        setSelectedBox(null);
        // Refresh host detail (counts) and the boxes list independently
        refetch();
        refetchBoxes();
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Error assigning box");
    }
  };
  
  const handleRemoveBox = async (boxId: string, boxName: string) => {
    if (window.confirm(`Are you sure you want to remove box "${boxName}" from this key host? This will unassign the box but not delete it.`)) {
      try {
        // Update box to remove key host assignment (set to null)
        const updateData = {
          id: boxId,
          key_host_id: null
        };

        const res: any = await updateBox(updateData);
        
          if (res?.success) {
          toast.success(res?.message || "Box removed successfully");
          // Refresh host detail (counts) and the boxes list independently
          refetch();
          refetchBoxes();
        }
      } catch (error: any) {
        toast.error(error?.response?.data?.message || "Error removing box");
      }
    }
  };
  
  const handleClick = (id: any) => {
    navigate(`/boxmanagementdetail/${id}`, {
      state: {
        id,
      },
    });
  };
  const handleClickKey = (id: any) => {
    navigate("/keymanagementdetail", {
      state: {
        id,
      },
    });
  };

  // Enable / Disable the key host store
  const handleToggleStatus = async () => {
    const isCurrentlyActive = hostDetail?.status === 1;
    const newStatus = isCurrentlyActive ? 0 : 1;
    if (isCurrentlyActive) {
      if (!window.confirm('Are you sure you want to disable this key host? They will no longer appear in the app.')) return;
    }
    try {
      const res: any = await updateStatus({ id: keyHostId, status: newStatus });
      if (res?.success) {
        toast.success(res?.message || 'Status updated successfully');
        // Patch cache so UI reflects the change immediately
        queryClient.setQueryData(
          [ENDPOINTS.GET_KEY_HOST_DETAILS, { id: keyHostId }],
          (old: any) => ({
            ...old,
            data: {
              ...old?.data,
              keyHostDetails: {
                ...old?.data?.keyHostDetails,
                status: newStatus,
              },
            },
          })
        );
        refetch();
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Error updating status');
    }
  };

  // Resend login credentials to a specific user by email
  const handleResendPassword = async (user: any) => {
    try {
      const res: any = await resendPass({ email: user.email });
      if (res?.success) {
        toast.success(res?.message || `Credentials resent to ${user.email}`);
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Error resending credentials');
    }
  };

  return (
    <div className="main-wrapper">
      <Header title="Key Host Details" />
      <CustomLaoder loader={isLoading}>
        <div className="content-wrapper">
          <section className="main-section profile-section">
            <div className="container">
              {/* Back Button */}
              <button
                className="btn primary-btn me-0 view-history-btn my-3"
                onClick={() => navigate(-1)}
              >
                <i className="fa-solid fa-chevron-left" />{" "}
                <span className="m-lg-1">Back</span>
              </button>

              {/* Key Host Information Card */}
              <div className="wrapper-card mb-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h3 className="mb-0">
                    <i className="fa-solid fa-store me-2" />
                    Key Host Information
                  </h3>
                  <div className="d-flex align-items-center gap-2">
                    {/* Active / Disabled status badge + toggle button */}
                    <span className={`c-pill ${hostDetail?.status === 1 ? 'c-pill--success' : 'c-pill--danger'}`}>
                      {hostDetail?.status === 1 ? 'Active' : 'Disabled'}
                    </span>
                    {!isEditingHost && (
                      <button
                        className={`btn btn-sm ${hostDetail?.status === 1 ? 'btn-outline-danger' : 'btn-outline-success'}`}
                        onClick={handleToggleStatus}
                        disabled={isTogglingStatus}
                        title={hostDetail?.status === 1 ? 'Disable this key host' : 'Enable this key host'}
                      >
                        {isTogglingStatus ? (
                          <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                        ) : (
                          <i className={`fa-solid ${hostDetail?.status === 1 ? 'fa-ban' : 'fa-circle-check'} me-1`} />
                        )}
                        {hostDetail?.status === 1 ? 'Disable' : 'Enable'}
                      </button>
                    )}
                    {!isEditingHost ? (
                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={handleEditHost}
                      >
                        <i className="fa-solid fa-pen-to-square me-2" />
                        Edit Info
                      </button>
                    ) : (
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-success btn-sm"
                          onClick={handleSaveHostInfo}
                          disabled={isUpdatingHost}
                        >
                          <i className="fa-solid fa-check me-2" />
                          {isUpdatingHost ? "Saving..." : "Save"}
                        </button>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={handleCancelEditHost}
                          disabled={isUpdatingHost}
                        >
                          <i className="fa-solid fa-times me-2" />
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="row gy-4">
                  {/* Left Column */}
                  <div className="col-12 col-lg-6">
                    <div className="info-card h-100">
                      <div className="info-detail mb-3">
                        <h5 className="text-muted mb-2">
                          <i className="fa-solid fa-building me-2" />
                          Store Name
                        </h5>
                        {isEditingHost ? (
                          <input
                            type="text"
                            className="form-control"
                            value={editedHostInfo?.storeName || ''}
                            onChange={(e) => setEditedHostInfo({...editedHostInfo, storeName: e.target.value})}
                          />
                        ) : (
                          <p className="mb-0 fs-5 fw-semibold">{hostDetail?.storeName || 'N/A'}</p>
                        )}
                      </div>

                      <div className="info-detail mb-3">
                        <h5 className="text-muted mb-2">
                          <i className="fa-solid fa-location-dot me-2" />
                          Address
                        </h5>
                        {isEditingHost ? (
                          <>
                            <textarea
                              className="form-control mb-2"
                              rows={2}
                              value={editedHostInfo?.address || ''}
                              onChange={(e) => setEditedHostInfo({...editedHostInfo, address: e.target.value})}
                            />
                            <label className="form-label small text-muted mb-1">Country</label>
                            <div className="form-control p-1">
                              <SortedCountryDropdown
                                name="countryName"
                                value={editedHostInfo?.countryName || ''}
                                onChange={(val: string) => setEditedHostInfo({...editedHostInfo, countryName: val})}
                              />
                            </div>
                          </>
                        ) : (
                          <>
                            <p className="mb-1">{hostDetail?.address || 'N/A'}</p>
                            <small className="text-muted">
                              <i className="fa-solid fa-globe me-1" />
                              {hostDetail?.countryName || <span className="fst-italic">Country not set</span>}
                            </small>
                          </>
                        )}
                      </div>

                      <div className="info-detail mb-3">
                        <h5 className="text-muted mb-2">
                          <i className="fa-solid fa-phone me-2" />
                          Phone Number
                        </h5>
                        {isEditingHost ? (
                          <div className="d-flex gap-2">
                            <input
                              type="text"
                              className="form-control"
                              style={{ width: '80px' }}
                              placeholder="+1"
                              value={editedHostInfo?.countryCode || ''}
                              onChange={(e) => setEditedHostInfo({...editedHostInfo, countryCode: e.target.value})}
                            />
                            <input
                              type="text"
                              className="form-control"
                              value={editedHostInfo?.phoneNumber || ''}
                              onChange={(e) => setEditedHostInfo({...editedHostInfo, phoneNumber: e.target.value})}
                            />
                          </div>
                        ) : (
                          <p className="mb-0">
                            (+{hostDetail?.countryCode}) {hostDetail?.phoneNumber}
                          </p>
                        )}
                      </div>

                      <div className="info-detail">
                        <h5 className="text-muted mb-2">
                          <i className="fa-solid fa-info-circle me-2" />
                          Store Info
                        </h5>
                        {isEditingHost ? (
                          <textarea
                            className="form-control"
                            rows={3}
                            value={editedHostInfo?.storeInfo || ''}
                            onChange={(e) => setEditedHostInfo({...editedHostInfo, storeInfo: e.target.value})}
                          />
                        ) : (
                          <p className="mb-0">{hostDetail?.storeInfo || 'N/A'}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="col-12 col-lg-6">
                    <div className="info-card h-100">
                      <div className="row gy-3 mb-4">
                        <div className="col-6">
                          <div className="stat-card text-center p-3 bg-light rounded">
                            <i className="fa-solid fa-key fs-3 text-primary mb-2" />
                            <h3 className="mb-1">{hostDetail?.totalKeys || 0}</h3>
                            <p className="mb-0 text-muted small">Keys Hosted</p>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="stat-card text-center p-3 bg-light rounded">
                            <i className="fa-solid fa-box fs-3 text-success mb-2" />
                            <h3 className="mb-1">{hostDetail?.totalBoxes || 0}</h3>
                            <p className="mb-0 text-muted small">Boxes Assigned</p>
                          </div>
                        </div>
                      </div>

                      <div className="info-detail">
                        <h5 className="text-muted mb-3">
                          <i className="fa-solid fa-clock me-2" />
                          Hours of Operation
                        </h5>
                        <div className="hours-list">
                          {isEditingHost ? (
                            // Edit mode for hours
                            editedHostInfo?.activeTimes?.map((time: any, index: number) => (
                              <div key={index} className="d-flex justify-content-between align-items-center py-2 border-bottom">
                                <span className="fw-semibold" style={{ minWidth: '100px' }}>
                                  {time?.day}
                                </span>
                                <div className="d-flex align-items-center gap-2">
                                  <div className="form-check">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      checked={!time?.closed}
                                      onChange={(e) => handleUpdateActiveTime(index, 'closed', !e.target.checked)}
                                    />
                                    <label className="form-check-label small">Open</label>
                                  </div>
                                  {!time?.closed && (
                                    <>
                                      <input
                                        type="time"
                                        className="form-control form-control-sm"
                                        style={{ width: '100px' }}
                                        value={time?.startTime || ''}
                                        onChange={(e) => handleUpdateActiveTime(index, 'startTime', e.target.value)}
                                      />
                                      <span>-</span>
                                      <input
                                        type="time"
                                        className="form-control form-control-sm"
                                        style={{ width: '100px' }}
                                        value={time?.endTime || ''}
                                        onChange={(e) => handleUpdateActiveTime(index, 'endTime', e.target.value)}
                                      />
                                    </>
                                  )}
                                </div>
                              </div>
                            ))
                          ) : (
                            // View mode for hours
                            hostDetail?.activeTimes?.map((time: any, index: number) => (
                              <div key={index} className="d-flex justify-content-between align-items-center py-2 border-bottom">
                                <span className="fw-semibold" style={{ minWidth: '100px' }}>
                                  {time?.day}
                                </span>
                                <span className="text-end">
                                  {time?.closed === false && time.startTime !== "" ? (
                                    <span className="text-success">
                                      {moment.utc(time.startTime, "HH:mm").format("LT")} - {moment.utc(time.endTime, "HH:mm").format("LT")}
                                    </span>
                                  ) : (
                                    <span className="text-danger">Closed</span>
                                  )}
                                </span>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Key Host Users Section */}
              <div className="wrapper-card mb-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h3 className="mb-0">
                    <i className="fa-solid fa-users me-2" />
                    Key Host Users
                  </h3>
                  <button
                    className="btn primary-btn me-0"
                    data-bs-toggle="modal"
                    data-bs-target="#addUserModal"
                  >
                    <i className="fa-solid fa-plus me-2" />
                    Add User
                  </button>
                </div>

                <div className="table-blk">
                  <table id="myTable" className="display border-0">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Login ID</th>
                        <th>Password</th>
                        <th>Role</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {GetKeyHostDetail?.data?.keyHostUsers?.map(
                        (user: any, index: number) => (
                          <tr key={index}>
                            <td>{user.userName}</td>
                            <td>{user.email}</td>
                            <td>{user.userLoginId}</td>
                            <td>{user.password}</td>
                            <td>
                              <span className="text-capitalize">
                                {user.role || 'staff'}
                              </span>
                            </td>
                            <td>
                              <span
                                className={`c-pill
                                ${
                                  user.status === 1
                                    ? "c-pill--success"
                                    : "c-pill--danger"
                                }`}
                              >
                                {user.status === 1 ? "Active" : "Suspended"}
                              </span>
                            </td>
                            <td>
                              <span className="table-action">
                                <a
                                  href="#"
                                  className="table-edit"
                                  data-bs-toggle="modal"
                                  data-bs-target="#editUserModal"
                                  title="Edit User"
                                  onClick={(e) => { e.preventDefault(); handleEditUser(user); }}
                                  style={{ cursor: 'pointer' }}
                                >
                                  <EditSvg />
                                </a>
                                <a
                                  href="#"
                                  className="table-delete"
                                  title="Delete User"
                                  onClick={(e) => { e.preventDefault(); handleDeleteUser(user); }}
                                  style={{ cursor: 'pointer' }}
                                >
                                  <i className="fa-solid fa-trash" />
                                </a>
                                <a
                                  href="#"
                                  title="Resend Login Credentials"
                                  onClick={(e) => { e.preventDefault(); handleResendPassword(user); }}
                                  style={{ cursor: 'pointer', color: '#72bcb8' }}
                                >
                                  <i className="fa-solid fa-envelope" />
                                </a>
                              </span>
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                  {GetKeyHostDetail?.data?.keyHostUsers?.length === 0 ? (
                    <div className="d-flex justify-content-center align-items-center my-3">
                      <p className="m-0">No users found</p>
                    </div>
                  ) : null}
                </div>
              </div>

              {/* Boxes Details Section */}
              <div className="wrapper-card mb-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h3 className="mb-0">
                    <i className="fa-solid fa-box me-2" />
                    Boxes Details
                  </h3>
                  <button
                    className="btn primary-btn me-0"
                    onClick={handleAssignBox}
                  >
                    <i className="fa-solid fa-plus me-2" />
                    Assign Box
                  </button>
                </div>
                <div className="table-blk">
                  <table id="myTable" className="display border-0">
                    <thead>
                      <tr>
                        <th>Box ID</th>
                        <th>Total Cubicles</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoadingBoxes ? (
                        <tr>
                          <td colSpan={4} className="text-center py-3">
                            <span className="spinner-border spinner-border-sm me-2" />Loading...
                          </td>
                        </tr>
                      ) : boxesData?.data?.boxes?.map(
                        (item: any, index: number) => (
                          <tr key={index}>
                            <td>{item.boxId}</td>
                            <td>{item.totalSlots}</td>
                            <td>
                              <span
                                className={`c-pill
                                ${
                                  item.status === 1
                                    ? "c-pill--success"
                                    : "c-pill--danger"
                                }`}
                              >
                                {item.status === 1 ? "Active" : "Suspended"}
                              </span>
                            </td>
                            <td>
                              <span className="table-action">
                                <a
                                  href="#"
                                  className="table-eye"
                                  onClick={(e) => { e.preventDefault(); handleClick(item?.Id || item?._id); }}
                                  style={{ cursor: 'pointer' }}
                                  title="View Box Details"
                                >
                                  <EyeSvg />
                                </a>
                                <a
                                  href="#"
                                  className="table-delete"
                                  onClick={(e) => { e.preventDefault(); handleRemoveBox(item?.Id || item?._id, item.boxId); }}
                                  style={{ cursor: 'pointer' }}
                                  title="Remove Box from Key Host"
                                >
                                  <i className="fa-solid fa-unlink" />
                                </a>
                              </span>
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                  {!isLoadingBoxes && boxesData?.data?.boxes?.length === 0 && (
                    <div className="d-flex justify-content-center align-items-center my-3">
                      <p className="m-0">No boxes found</p>
                    </div>
                  )}
                  {/* Boxes pagination */}
                  {boxesTotalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center mt-3 px-1">
                      <small className="text-muted">
                        Page {boxesPage} of {boxesTotalPages} ({boxesTotalCount} boxes)
                      </small>
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => setBoxesPage((p) => Math.max(1, p - 1))}
                          disabled={boxesPage <= 1}
                        >
                          <i className="fa-solid fa-chevron-left" />
                        </button>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => setBoxesPage((p) => Math.min(boxesTotalPages, p + 1))}
                          disabled={boxesPage >= boxesTotalPages}
                        >
                          <i className="fa-solid fa-chevron-right" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Keys Details Section */}
              <div className="wrapper-card mb-4">
                <div className="sub-heading mb-3">
                  <h3>
                    <i className="fa-solid fa-key me-2" />
                    Keys Details
                  </h3>
                </div>
                <div className="table-blk">
                  <table id="myTable" className="display border-0">
                    <thead>
                      <tr>
                        <th>Key Name</th>
                        <th>Box ID</th>
                        <th>Cubicle Location</th>
                        <th>Key Ring Code</th>
                        <th>Subscription Validity</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoadingKeys ? (
                        <tr>
                          <td colSpan={6} className="text-center py-3">
                            <span className="spinner-border spinner-border-sm me-2" />Loading...
                          </td>
                        </tr>
                        ) : keysData?.data?.keys?.map(
                        (item: any, index: number) => (
                          <tr key={index}>
                            <td>{item?.keyName}</td>
                            <td>{item?.boxId}</td>
                            <td>{item?.cubicalLocation}</td>
                            <td>{item?.keyringCode || 'N/A'}</td>
                            <td>
                              <span
                                className={`c-pill 
                                ${
                                  item?.isSubscriptionActive === true
                                    ? "c-pill--success"
                                    : "c-pill--danger"
                                }`}
                              >
                                {item?.isSubscriptionActive === true
                                  ? "Active"
                                  : "Suspended"}
                              </span>
                            </td>
                            <td>
                              <span className="table-action">
                                <a
                                  href="#"
                                  className="table-eye"
                                  onClick={(e) => { e.preventDefault(); handleClickKey(item?.Id); }}
                                  style={{ cursor: 'pointer' }}
                                >
                                  <EyeSvg />
                                </a>
                              </span>
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                  {!isLoadingKeys && keysData?.data?.keys?.length === 0 && (
                    <div className="d-flex justify-content-center align-items-center my-3">
                      <p className="m-0">No keys found</p>
                    </div>
                  )}
                  {/* Keys pagination */}
                  {keysTotalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center mt-3 px-1">
                      <small className="text-muted">
                        Page {keysPage} of {keysTotalPages} ({keysTotalCount} keys)
                      </small>
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => setKeysPage((p) => Math.max(1, p - 1))}
                          disabled={keysPage <= 1}
                        >
                          <i className="fa-solid fa-chevron-left" />
                        </button>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => setKeysPage((p) => Math.min(keysTotalPages, p + 1))}
                          disabled={keysPage >= keysTotalPages}
                        >
                          <i className="fa-solid fa-chevron-right" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* Add User Modal */}
          <div
            className="modal fade common-modal"
            id="addUserModal"
            tabIndex={-1}
            aria-labelledby="addUser"
            aria-hidden="true"
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-body">
                  <button
                    id="close-add-btn"
                    type="button"
                    className="btn-close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  />
                  <div className="common-box-body">
                    <h2>Add Key Host User</h2>
                    <div className="common-modal-form">
                      <div className="common-modal-field">
                        <label>Name</label>
                        <input
                          placeholder="Enter user name"
                          className="form-control"
                          type="text"
                          name="userName"
                          value={newUser.userName}
                          onChange={(e) => setNewUser({...newUser, userName: e.target.value})}
                        />
                      </div>
                      <div className="common-modal-field">
                        <label>Email</label>
                        <input
                          placeholder="Enter email"
                          className="form-control"
                          type="email"
                          name="email"
                          value={newUser.email}
                          onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                        />
                      </div>
                      <div className="common-modal-field">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <label className="mb-0">Login ID</label>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={handleGenerateLoginId}
                            style={{ fontSize: '12px', padding: '2px 8px' }}
                          >
                            <i className="fa-solid fa-wand-magic-sparkles me-1" />
                            Generate
                          </button>
                        </div>
                        <input
                          placeholder="Enter login ID"
                          className="form-control"
                          type="text"
                          name="loginId"
                          value={newUser.loginId}
                          onChange={(e) => setNewUser({...newUser, loginId: e.target.value})}
                        />
                      </div>
                      <div className="common-modal-field">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <label className="mb-0">Password</label>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={handleGeneratePassword}
                            style={{ fontSize: '12px', padding: '2px 8px' }}
                          >
                            <i className="fa-solid fa-wand-magic-sparkles me-1" />
                            Generate
                          </button>
                        </div>
                        <input
                          placeholder="Enter password"
                          className="form-control"
                          type="text"
                          name="password"
                          value={newUser.password}
                          onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                        />
                      </div>
                      <div className="common-modal-field">
                        <label>Role</label>
                        <select
                          className="form-control"
                          name="role"
                          value={newUser.role}
                          onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                        >
                          <option value="staff">Staff</option>
                          <option value="owner">Owner</option>
                        </select>
                      </div>
                    </div>
                    <div className="common-modal-btns">
                      <button
                        className="btn primary-btn me-0"
                        onClick={handleAddUser}
                        disabled={isAdding}
                      >
                        {isAdding ? "Adding..." : "Add User"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Edit User Modal */}
          <div
            className="modal fade common-modal"
            id="editUserModal"
            tabIndex={-1}
            aria-labelledby="editUser"
            aria-hidden="true"
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-body">
                  <button
                    id="close-edit-btn"
                    type="button"
                    className="btn-close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  />
                  <div className="common-box-body">
                    <h2>Edit Key Host User</h2>
                    <div className="common-modal-form">
                      <div className="common-modal-field">
                        <label>Name</label>
                        <input
                          placeholder="Enter user name"
                          className="form-control"
                          type="text"
                          name="userName"
                          value={selectedUser?.userName || ''}
                          onChange={(e) => setSelectedUser({...selectedUser, userName: e.target.value})}
                        />
                      </div>
                      <div className="common-modal-field">
                        <label>Email</label>
                        <input
                          placeholder="Enter email"
                          className="form-control"
                          type="email"
                          name="email"
                          value={selectedUser?.email || ''}
                          onChange={(e) => setSelectedUser({...selectedUser, email: e.target.value})}
                        />
                      </div>
                      <div className="common-modal-field">
                        <label>Login ID</label>
                        <input
                          placeholder="Enter login ID"
                          className="form-control"
                          type="text"
                          name="loginId"
                          value={selectedUser?.userLoginId || ''}
                          onChange={(e) => setSelectedUser({...selectedUser, userLoginId: e.target.value})}
                        />
                      </div>
                      <div className="common-modal-field">
                        <label>Password</label>
                        <input
                          placeholder="Enter new password (leave blank to keep current)"
                          className="form-control"
                          type="password"
                          name="password"
                          onChange={(e) => setSelectedUser({...selectedUser, password: e.target.value})}
                        />
                      </div>
                      <div className="common-modal-field">
                        <label>Role</label>
                        <select
                          className="form-control"
                          name="role"
                          value={selectedUser?.role || 'staff'}
                          onChange={(e) => setSelectedUser({...selectedUser, role: e.target.value})}
                        >
                          <option value="staff">Staff</option>
                          <option value="owner">Owner</option>
                        </select>
                      </div>
                      <div className="common-modal-field">
                        <label>Status</label>
                        <select
                          className="form-control"
                          name="status"
                          value={selectedUser?.status || 1}
                          onChange={(e) => setSelectedUser({...selectedUser, status: parseInt(e.target.value)})}
                        >
                          <option value="1">Active</option>
                          <option value="0">Suspended</option>
                        </select>
                      </div>
                    </div>
                    <div className="common-modal-btns">
                      <button
                        className="btn primary-btn me-0"
                        onClick={handleUpdateUser}
                        disabled={isUpdating}
                      >
                        {isUpdating ? "Updating..." : "Update User"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Assign Box Modal */}
          <div
            className={`modal fade common-modal ${showAssignBoxModal ? 'show d-block' : ''}`}
            id="assignBoxModal"
            tabIndex={-1}
            aria-labelledby="assignBox"
            aria-hidden={!showAssignBoxModal}
            style={{ backgroundColor: showAssignBoxModal ? 'rgba(0,0,0,0.5)' : 'transparent' }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-body">
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setShowAssignBoxModal(false);
                      setSelectedBox(null);
                    }}
                    aria-label="Close"
                  />
                  <div className="common-box-body">
                    <h2>Assign Box to Key Host</h2>
                    <div className="common-modal-form">
                      <div className="common-modal-field">
                        <label>Select Box</label>
                        <select
                          className="form-control"
                          value={selectedBox || ''}
                          onChange={(e) => setSelectedBox(e.target.value)}
                        >
                          <option value="">-- Select a box --</option>
                          {boxListData?.data?.boxes
                            ?.filter((box: any) => !box.keyHostId || box.keyHostId === '')
                            ?.map((box: any) => {
                              // API transformer converts _id to Id (capital I)
                              const boxId = box.Id || box._id;
                              return (
                                <option key={boxId} value={boxId}>
                                  {box.boxId} - {box.totalSlots} cubicles
                                </option>
                              );
                            })}
                        </select>
                        <small className="text-muted mt-2 d-block">
                          Only unassigned boxes are shown
                        </small>
                        {boxListData?.data?.boxes && boxListData.data.boxes.length === 0 && (
                          <small className="text-danger mt-2 d-block">
                            No unassigned boxes available
                          </small>
                        )}
                      </div>
                    </div>
                    <div className="common-modal-btns">
                      <button
                        className="btn primary-btn me-0"
                        onClick={handleConfirmAssignBox}
                        disabled={isAssigningBox || !selectedBox}
                      >
                        {isAssigningBox ? "Assigning..." : "Assign Box"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CustomLaoder>
    </div>
  );
};

export default KeyHostDetailScreen;
